import type { StructureDef } from '@playnets/sim';

const PI = Math.PI;

/**
 * Eight general structures plus one exclusive per planet.
 *
 * `jewelCost` is the permanent unlock price, paid once out of jewels earned by
 * winning levels. Two are starters at 0 — a surface gun and a ring gun — so
 * both layers are usable before the player has won anything to spend.
 *
 * Everything costs between 3 and 24. Small whole numbers on purpose: you should
 * be able to look at your juice and a card and know whether you can afford it
 * without doing arithmetic. Coverage is an arc
 * plus a range band, and the total arc you can field is deliberately well short
 * of 360° — the ring spin is supposed to be a bet, not a formality (§2).
 */
export const STRUCTURES: readonly StructureDef[] = [
  {
    id: 'railgun',
    jewelCost: 2,
    name: 'Railgun',
    kind: 'turret',
    layer: 'surface',
    text: 'Long reach, narrow arc, single target. The answer to a parked Carrier.',
    colour: 0xff6b35,
    mergesInto: 'siege-driver',
    tiers: [
      { cost: 6, damage: 16, rate: 1.1, spread: PI / 5, range: 190, muzzle: 620, splash: 0 },
      { cost: 8, damage: 27, rate: 1.25, spread: PI / 4.5, range: 215, muzzle: 660, splash: 0 },
      { cost: 13, damage: 44, rate: 1.4, spread: PI / 4, range: 245, muzzle: 700, splash: 0 },
      { cost: 22, damage: 72, rate: 1.55, spread: PI / 3.7, range: 275, muzzle: 740, splash: 0 },
      { cost: 36, damage: 118, rate: 1.7, spread: PI / 3.5, range: 305, muzzle: 780, splash: 0 },
    ],
  },
  {
    id: 'flak',
    jewelCost: 0,
    name: 'Flak Battery',
    kind: 'turret',
    layer: 'surface',
    text: 'Wide arc, short reach, splash. Built for drone fans.',
    colour: 0xffd166,
    mergesInto: 'storm-battery',
    tiers: [
      { cost: 6, damage: 7, rate: 2.6, spread: PI / 2.2, range: 105, muzzle: 480, splash: 22 },
      { cost: 8, damage: 11, rate: 3.0, spread: PI / 2, range: 118, muzzle: 500, splash: 26 },
      { cost: 12, damage: 17, rate: 3.4, spread: PI / 1.8, range: 132, muzzle: 520, splash: 32 },
      { cost: 19, damage: 27, rate: 3.8, spread: PI / 1.7, range: 146, muzzle: 540, splash: 38 },
      { cost: 31, damage: 42, rate: 4.2, spread: PI / 1.6, range: 160, muzzle: 560, splash: 45 },
    ],
  },
  {
    id: 'beam-lance',
    jewelCost: 4,
    name: 'Beam Lance',
    kind: 'beam',
    layer: 'surface',
    text: 'Hitscan. Slow, expensive, and the only thing that reliably eats an Asteroid.',
    colour: 0xff8f5a,
    tiers: [
      { cost: 9, damage: 40, rate: 0.62, spread: PI / 7, range: 200, muzzle: 0, splash: 0 },
      { cost: 12, damage: 66, rate: 0.7, spread: PI / 6.5, range: 225, muzzle: 0, splash: 0 },
      { cost: 18, damage: 108, rate: 0.78, spread: PI / 6, range: 255, muzzle: 0, splash: 0 },
      { cost: 29, damage: 177, rate: 0.86, spread: PI / 5.7, range: 285, muzzle: 0, splash: 0 },
      { cost: 47, damage: 290, rate: 0.94, spread: PI / 5.4, range: 315, muzzle: 0, splash: 0 },
    ],
  },
  {
    id: 'radar-mast',
    jewelCost: 3,
    name: 'Radar Mast',
    kind: 'support',
    layer: 'surface',
    text: 'Reveals Wraiths and sharpens everything nearby. No guns of its own.',
    colour: 0xc9a227,
    radar: true,
    support: 'damage-aura',
    tiers: [
      { cost: 4, damage: 0, rate: 0, spread: PI * 2, range: 140, muzzle: 0, splash: 0, auraMult: 1.12, auraRadius: 0.9 },
      { cost: 6, damage: 0, rate: 0, spread: PI * 2, range: 180, muzzle: 0, splash: 0, auraMult: 1.2, auraRadius: 1.1 },
      { cost: 10, damage: 0, rate: 0, spread: PI * 2, range: 230, muzzle: 0, splash: 0, auraMult: 1.3, auraRadius: 1.4 },
      { cost: 16, damage: 0, rate: 0, spread: PI * 2, range: 285, muzzle: 0, splash: 0, auraMult: 1.42, auraRadius: 1.7 },
      { cost: 26, damage: 0, rate: 0, spread: PI * 2, range: 345, muzzle: 0, splash: 0, auraMult: 1.55, auraRadius: 2.0 },
    ],
  },
  {
    id: 'satellite',
    jewelCost: 0,
    name: 'Satellite',
    kind: 'turret',
    layer: 'ring',
    text: 'The ring workhorse. Its arc sweeps every bearing in turn.',
    colour: 0xff6b35,
    mergesInto: 'weapons-platform',
    tiers: [
      { cost: 7, damage: 13, rate: 1.8, spread: PI / 3, range: 130, muzzle: 560, splash: 0, mass: 0.35 },
      { cost: 10, damage: 21, rate: 2.0, spread: PI / 2.8, range: 145, muzzle: 590, splash: 0, mass: 0.4 },
      { cost: 14, damage: 34, rate: 2.2, spread: PI / 2.6, range: 165, muzzle: 620, splash: 8, mass: 0.45 },
      { cost: 23, damage: 55, rate: 2.4, spread: PI / 2.45, range: 185, muzzle: 650, splash: 12, mass: 0.5 },
      { cost: 37, damage: 89, rate: 2.6, spread: PI / 2.3, range: 205, muzzle: 680, splash: 16, mass: 0.55 },
    ],
  },
  {
    id: 'mine-cluster',
    jewelCost: 2,
    name: 'Mine Cluster',
    kind: 'mine',
    layer: 'ring',
    text: 'Contact charges. Cheap, consumable, and gone when they are spent.',
    colour: 0xf29d1f,
    tiers: [
      { cost: 4, damage: 60, rate: 0, spread: PI / 4, range: 0, muzzle: 0, splash: 40, charges: 3, mass: 0.15 },
      { cost: 5, damage: 95, rate: 0, spread: PI / 3.4, range: 0, muzzle: 0, splash: 48, charges: 4, mass: 0.18 },
      { cost: 8, damage: 150, rate: 0, spread: PI / 3, range: 0, muzzle: 0, splash: 58, charges: 5, mass: 0.22 },
      { cost: 13, damage: 237, rate: 0, spread: PI / 2.7, range: 0, muzzle: 0, splash: 70, charges: 6, mass: 0.26 },
      { cost: 21, damage: 374, rate: 0, spread: PI / 2.4, range: 0, muzzle: 0, splash: 84, charges: 7, mass: 0.3 },
    ],
  },
  {
    id: 'moon',
    jewelCost: 5,
    name: 'Moon',
    kind: 'moon',
    layer: 'ring',
    text: 'A body, not a gun. Deletes a swarm on contact and barely scratches a Cruiser. Heavy — the ring will turn like it.',
    colour: 0xb9bfc9,
    tiers: [
      { cost: 11, damage: 130, rate: 0, spread: 0, range: 0, muzzle: 0, splash: 0, moonArc: 0.16, mass: 1.6 },
      { cost: 15, damage: 210, rate: 0, spread: 0, range: 0, muzzle: 0, splash: 0, moonArc: 0.2, mass: 2.4 },
      { cost: 22, damage: 340, rate: 0, spread: 0, range: 0, muzzle: 0, splash: 0, moonArc: 0.25, mass: 3.4 },
      { cost: 36, damage: 545, rate: 0, spread: 0, range: 0, muzzle: 0, splash: 0, moonArc: 0.31, mass: 4.2 },
      { cost: 58, damage: 870, rate: 0, spread: 0, range: 0, muzzle: 0, splash: 0, moonArc: 0.38, mass: 5.2 },
    ],
  },
  {
    id: 'repair-drone',
    jewelCost: 4,
    name: 'Repair Drone',
    kind: 'support',
    layer: 'ring',
    text: 'Slowly gives Core Integrity back. Light on the ring.',
    colour: 0x7fd4e8,
    support: 'core-repair',
    tiers: [
      { cost: 8, damage: 1.1, rate: 0, spread: 0, range: 0, muzzle: 0, splash: 0, mass: 0.2 },
      { cost: 11, damage: 1.9, rate: 0, spread: 0, range: 0, muzzle: 0, splash: 0, mass: 0.22 },
      { cost: 16, damage: 3.2, rate: 0, spread: 0, range: 0, muzzle: 0, splash: 0, mass: 0.25 },
      { cost: 23, damage: 5.4, rate: 0, spread: 0, range: 0, muzzle: 0, splash: 0, mass: 0.28 },
      { cost: 37, damage: 9.0, rate: 0, spread: 0, range: 0, muzzle: 0, splash: 0, mass: 0.32 },
    ],
  },

  /* --- Merge results -------------------------------------------------
   *
   * Not buildable and not for sale. You reach one by taking a gun to the top
   * of its ladder and feeding it one more of itself, and each is a genuine
   * change of job rather than a bigger number:
   *
   *   Railgun    single-target sniper  ->  Siege Driver  splash sniper
   *   Flak       a wide arc            ->  Storm Battery every bearing at once
   *   Satellite  a gun                 ->  Weapons Platform  gun that also
   *                                        reveals cloaks and sharpens the
   *                                        mounts either side of it
   *
   * Three tiers rather than five: by the time you own one, the level is
   * usually most of the way through.
   * ------------------------------------------------------------------ */

  {
    id: 'siege-driver',
    jewelCost: 0,
    mergeOnly: true,
    name: 'Siege Driver',
    kind: 'turret',
    layer: 'surface',
    text: 'What a Railgun becomes. Everything it hits takes the neighbours with it.',
    colour: 0xff8f5a,
    tiers: [
      { cost: 58, damage: 165, rate: 1.8, spread: PI / 3.2, range: 330, muzzle: 820, splash: 34 },
      { cost: 78, damage: 265, rate: 1.95, spread: PI / 3, range: 360, muzzle: 860, splash: 42 },
      { cost: 104, damage: 425, rate: 2.1, spread: PI / 2.8, range: 395, muzzle: 900, splash: 52 },
    ],
  },
  {
    id: 'storm-battery',
    jewelCost: 0,
    mergeOnly: true,
    name: 'Storm Battery',
    kind: 'turret',
    layer: 'surface',
    text: 'What a Flak Battery becomes. No arc left to aim — it covers the whole sky.',
    colour: 0xffd429,
    tiers: [
      { cost: 50, damage: 46, rate: 4.6, spread: PI * 2, range: 175, muzzle: 580, splash: 50 },
      { cost: 68, damage: 72, rate: 5.0, spread: PI * 2, range: 192, muzzle: 600, splash: 58 },
      { cost: 92, damage: 114, rate: 5.4, spread: PI * 2, range: 212, muzzle: 620, splash: 68 },
    ],
  },
  {
    id: 'weapons-platform',
    jewelCost: 0,
    mergeOnly: true,
    name: 'Weapons Platform',
    kind: 'turret',
    layer: 'ring',
    text: 'What a Satellite becomes. Sees through cloaks and sharpens whatever orbits beside it.',
    colour: 0xff6b35,
    radar: true,
    support: 'damage-aura',
    tiers: [
      { cost: 60, damage: 120, rate: 2.8, spread: PI / 2.1, range: 230, muzzle: 700, splash: 22, mass: 0.7, auraMult: 1.18, auraRadius: 1.0 },
      { cost: 82, damage: 190, rate: 3.0, spread: PI / 2, range: 255, muzzle: 730, splash: 28, mass: 0.78, auraMult: 1.26, auraRadius: 1.2 },
      { cost: 110, damage: 300, rate: 3.2, spread: PI / 1.9, range: 280, muzzle: 760, splash: 35, mass: 0.86, auraMult: 1.35, auraRadius: 1.45 },
    ],
  },

  /* --- Planet exclusives ------------------------------------------- */

  {
    id: 'shield-pylon',
    jewelCost: 3,
    name: 'Shield Pylon',
    kind: 'support',
    layer: 'surface',
    text: 'Terra only. Soaks a share of every hit that reaches the crust.',
    colour: 0x6fa094,
    support: 'core-shield',
    exclusiveTo: 'terra',
    tiers: [
      { cost: 7, damage: 0, rate: 0, spread: PI * 2, range: 0, muzzle: 0, splash: 0, auraMult: 0.82 },
      { cost: 10, damage: 0, rate: 0, spread: PI * 2, range: 0, muzzle: 0, splash: 0, auraMult: 0.72 },
      { cost: 14, damage: 0, rate: 0, spread: PI * 2, range: 0, muzzle: 0, splash: 0, auraMult: 0.6 },
      { cost: 20, damage: 0, rate: 0, spread: PI * 2, range: 0, muzzle: 0, splash: 0, auraMult: 0.5 },
      { cost: 32, damage: 0, rate: 0, spread: PI * 2, range: 0, muzzle: 0, splash: 0, auraMult: 0.42 },
    ],
  },
  {
    id: 'magma-vent',
    jewelCost: 3,
    name: 'Magma Vent',
    kind: 'turret',
    layer: 'surface',
    text: 'Ignis only. Short, furious, and it sets the ground on fire.',
    colour: 0xffd429,
    exclusiveTo: 'ignis',
    tiers: [
      { cost: 8, damage: 12, rate: 3.2, spread: PI / 2.6, range: 92, muzzle: 380, splash: 30 },
      { cost: 10, damage: 19, rate: 3.6, spread: PI / 2.4, range: 104, muzzle: 400, splash: 36 },
      { cost: 16, damage: 30, rate: 4.0, spread: PI / 2.2, range: 118, muzzle: 420, splash: 44 },
      { cost: 25, damage: 48, rate: 4.4, spread: PI / 2.1, range: 133, muzzle: 440, splash: 53 },
      { cost: 40, damage: 76, rate: 4.8, spread: PI / 2, range: 150, muzzle: 460, splash: 62 },
    ],
  },
  {
    id: 'cryo-lance',
    jewelCost: 3,
    name: 'Cryo Lance',
    kind: 'beam',
    layer: 'both',
    text: 'Glacia only. Hitscan, and everything it touches slows to a crawl.',
    colour: 0x7fd4e8,
    exclusiveTo: 'glacia',
    tiers: [
      { cost: 8, damage: 24, rate: 1.0, spread: PI / 5.5, range: 175, muzzle: 0, splash: 0, mass: 0.3 },
      { cost: 12, damage: 39, rate: 1.15, spread: PI / 5, range: 198, muzzle: 0, splash: 0, mass: 0.34 },
      { cost: 17, damage: 62, rate: 1.3, spread: PI / 4.6, range: 222, muzzle: 0, splash: 0, mass: 0.38 },
      { cost: 26, damage: 99, rate: 1.45, spread: PI / 4.3, range: 248, muzzle: 0, splash: 0, mass: 0.42 },
      { cost: 42, damage: 158, rate: 1.6, spread: PI / 4, range: 275, muzzle: 0, splash: 0, mass: 0.46 },
    ],
  },
  {
    id: 'drone-bay',
    jewelCost: 4,
    name: 'Drone Bay',
    kind: 'drone-bay',
    layer: 'ring',
    text: 'Machina only. Keeps two escorts in the air permanently.',
    colour: 0xffd166,
    exclusiveTo: 'machina',
    tiers: [
      { cost: 12, damage: 9, rate: 0.5, spread: PI * 2, range: 150, muzzle: 520, splash: 0, mass: 0.5 },
      { cost: 16, damage: 15, rate: 0.6, spread: PI * 2, range: 170, muzzle: 540, splash: 0, mass: 0.55 },
      { cost: 24, damage: 24, rate: 0.7, spread: PI * 2, range: 195, muzzle: 560, splash: 0, mass: 0.6 },
      { cost: 38, damage: 38, rate: 0.8, spread: PI * 2, range: 220, muzzle: 580, splash: 0, mass: 0.65 },
      { cost: 60, damage: 60, rate: 0.9, spread: PI * 2, range: 248, muzzle: 600, splash: 0, mass: 0.7 },
    ],
  },
  {
    id: 'gravity-tether',
    jewelCost: 3,
    name: 'Gravity Tether',
    kind: 'turret',
    layer: 'ring',
    text: 'Umbra only. Drags what it hits off its bearing. Very heavy.',
    colour: 0xd98ec4,
    exclusiveTo: 'umbra',
    tiers: [
      { cost: 10, damage: 10, rate: 1.4, spread: PI / 2.4, range: 160, muzzle: 300, splash: 18, mass: 1.2 },
      { cost: 14, damage: 16, rate: 1.6, spread: PI / 2.2, range: 180, muzzle: 320, splash: 22, mass: 1.4 },
      { cost: 20, damage: 26, rate: 1.8, spread: PI / 2, range: 205, muzzle: 340, splash: 28, mass: 1.6 },
      { cost: 32, damage: 42, rate: 2.0, spread: PI / 1.9, range: 232, muzzle: 360, splash: 34, mass: 1.8 },
      { cost: 51, damage: 67, rate: 2.2, spread: PI / 1.8, range: 260, muzzle: 380, splash: 42, mass: 2.0 },
    ],
  },
];
